import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import useKpi from '../../hooks/kpis/useKpi'
import Grid from '@mui/material/Grid'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import KPI from '../../components/KPI'
import { a11yProps, formatDate, isValidDate } from '../../utils/helpers'
import { kpiTabs } from '../../utils/kpiTabs'
import { useParams } from 'react-router-dom'
import { useStore } from '../../hooks/globals/useStore'
import shallow from 'zustand/shallow'
import TabPanel from '../../components/TabPanel'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import DateRangeIcon from '@mui/icons-material/DateRange'
import Popover from '@mui/material/Popover'
import { DateRange } from 'react-date-range'
import isValid from 'date-fns/isValid'
import isBefore from 'date-fns/isBefore'
import isSameDay from 'date-fns/isSameDay'
import Alert from '@mui/material/Alert'

const Drilldown = () => {
  const { kpiTimeSpan, setKpiTimeSpan, kpiStartDate, kpiEndDate, kpiPreviousStartDate, kpiPreviousEndDate, kpiDateRange, setKpiDates, timezone } = useStore((state) => ({
    kpiTimeSpan: state.kpiTimeSpan,
    setKpiTimeSpan: state.setKpiTimeSpan,
    kpiStartDate: state.kpiStartDate,
    kpiEndDate: state.kpiEndDate,
    kpiPreviousStartDate: state.kpiPreviousStartDate,
    kpiPreviousEndDate: state.kpiPreviousEndDate,
    kpiDateRange: state.kpiDateRange,
    setKpiDates: state.setKpiDates,
    timezone: state.timezone
  }), shallow)
  
  const [state, setState] = useState('All')
  const [anchorEl, setAnchorEl] = useState(null)
  const [displayCalendar, setDisplayCalendar] = useState(false)
  const [dateRangeError, setDateRangeError] = useState(false)

  const { id } = useParams()

  const handleChange = (event, newValue) => {
    setKpiTimeSpan(newValue)
  }

  const handleChangeState = (event) => {
    setState(event.target.value)
  }

  const { data: kpi, isLoading: loadingKpi, isSuccess: loadedKpi, refetch: fetchKpi, isRefetching, isStale } = useKpi(id, state, kpiStartDate, kpiEndDate, kpiPreviousStartDate, kpiPreviousEndDate, timezone)

  useEffect(() => {
    if (kpiTimeSpan >= 0 && state && isStale) {
      fetchKpi()
    }
  }, [state, kpiTimeSpan, isStale])

  const handleOpenCalendar = (e) => {
    setAnchorEl(e.currentTarget)
    setDisplayCalendar(true)
  }

  const handleCloseCalendar = () => {
    setDisplayCalendar(false)
    setAnchorEl(null)
  }

  const handleDateRangeChange = (event) => {
    const inputValue = event.target.value
    let [startDate, endDate] = inputValue.split('-').map((elm) => elm.trim())
    
    let dateStart = new Date(startDate)
    dateStart = isValid(dateStart) ? dateStart : undefined

    let dateEnd = new Date(endDate)
    dateEnd = isValid(dateEnd) ? dateEnd: undefined
    if (isValidDate(startDate) && isValidDate(endDate)) {
      setDateRangeError(false)
      if (isBefore(dateStart, dateEnd) || isSameDay(dateStart, dateEnd)) {
        fetchKpi()
      } else {
        setDateRangeError(true)
      }
    } else {
      setDateRangeError(true)
    }

    setKpiDates({ 
      startDate: dateStart,
      endDate: dateEnd,
      dateRange: inputValue
    })
  }

  const handleSelectDateRange = ({ selection }) => {
    let { startDate, endDate } = selection

    let dateStart = new Date(startDate)
    dateStart = isValid(dateStart) ? dateStart : undefined
    let dateEnd = new Date(endDate)
    dateEnd = isValid(dateEnd) ? dateEnd: undefined

    let inputValue = ''
    if (dateStart) inputValue += formatDate(dateStart, 'MM/dd/yyyy')
    if (dateEnd) inputValue += ' - ' + formatDate(dateEnd, 'MM/dd/yyyy')

    if (isValidDate(formatDate(dateStart, 'MM/dd/yyyy')) && isValidDate(formatDate(dateEnd, 'MM/dd/yyyy'))) {
      setDateRangeError(false)
      if (isBefore(dateStart, dateEnd) || isSameDay(dateStart, dateEnd)) {
        fetchKpi()
      } else {
        setDateRangeError(true)
      }
    } else {
      setDateRangeError(true)
    }

    setKpiDates({ 
      startDate: dateStart,
      endDate: dateEnd,
      dateRange: inputValue
    })
  }

  return (
    <div>
      <Box component='div' sx={{ display: 'flex', flexDirection: { lg: 'row', xs: 'column' } }}>
        <Typography
          variant='h4'
          sx={{
            color: '#27417A',
            fontWeight: 'bold'
          }}
        >
          KPI Dashboard&nbsp;
        </Typography>
        <Typography
          variant='h4'
          sx={{
            color: '#3A6DAA',
            fontWeight: 'bold'
          }}
        >
          COVID-19&nbsp;
        </Typography> 
        <Typography
          variant='h4'
          sx={{
            color: '#27417A',
            fontWeight: 'bold'
          }}
        >
          Total Recorded Results
        </Typography> 
      </Box>
      <Box sx={{ width: '100%' }}>
        <Tabs 
          value={kpiTimeSpan} 
          onChange={handleChange}
          aria-label='nav tabs kpi'
          TabIndicatorProps={{
            style: {
              display: 'none'
            }
          }}
          sx={{
            ml: -2
          }}
        >
          {
            kpiTabs.map((tab, index) => (
              <Tab
                key={tab.label}
                label={tab.label}
                sx={{
                  textDecoration: kpiTimeSpan === index ? 'none' : 'underline',
                  color: '#27417A !important',
                  fontWeight: kpiTimeSpan === index ? 'bold' : '',
                }}
                value={index}
                disableFocusRipple
                disableRipple
                disableTouchRipple
                {...a11yProps(index)}
              />
            ))
          }
        </Tabs>
      </Box>
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={2}>
          <Grid
            item
            md={3}
            xs={12}
          >
            <FormControl fullWidth>
              <InputLabel id="state-select-label">State</InputLabel>
              <Select
                labelId="state-select-label"
                id="state-select"
                value={state}
                label="State"
                onChange={handleChangeState}
              >
                <MenuItem value='All'>All</MenuItem>
                <MenuItem value='CO'>Colorado</MenuItem>
                <MenuItem value='WI'>Wisconsin</MenuItem>
                <MenuItem value='Other'>Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {
            kpiTimeSpan === 5 && (
              <Grid
                item
                md={4}
                xs={12}
              >
                <TextField
                  label='Date Range'
                  fullWidth
                  value={kpiDateRange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end' onClick={handleOpenCalendar}>
                        <IconButton>
                          <DateRangeIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  onChange={handleDateRangeChange}
                />
                <Popover
                  open={displayCalendar}
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                  }}
                  onClose={handleCloseCalendar}
                >
                  <Box>
                    <DateRange 
                      ranges={[{
                        startDate: kpiStartDate,
                        endDate: kpiEndDate,
                        key: 'selection'
                      }]}
                      rangeColors={['#3A6DAA']}
                      onChange={handleSelectDateRange}
                      maxDate={new Date()}
                      showMonthAndYearPickers
                      moveRangeOnFirstSelection={false}
                      showDateDisplay={false}
                    />
                  </Box>
                </Popover>
              </Grid>
            )
          }
        </Grid>
      </Box>
      <TabPanel
        value={kpiTimeSpan}
        index={0}
      >
        <Box
          component={Grid}
          container 
          spacing={2}
          sx={{
            display: { md: 'flex', xs: 'center' },
            mt: 3
          }}
        >
          {
            (loadingKpi || isRefetching) && (
              <Grid
                item
                lg={12}
                md={12}
              >
                <CircularProgress />
              Loading KPIs
              </Grid>
            )
          }
          {
            (loadedKpi && !isRefetching) && (
              <>
                <Grid
                  item
                  lg={3} 
                  md={4}
                >
                  <KPI
                    kpiId={parseInt(id, 10)} 
                    name='Total Recorded Results' 
                    current={kpi.current.total}
                    previous={kpi.previous.total}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
                <Grid
                  item
                  lg={3} 
                  md={4}
                >
                  <KPI
                    kpiId={parseInt(id, 10)} 
                    name='Number of Positive Tests' 
                    current={kpi.current.positive}
                    previous={kpi.previous.positive}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
              </>
            )
          }
        </Box>
      </TabPanel>
      <TabPanel
        value={kpiTimeSpan}
        index={1}
      >
        <Box
          component={Grid}
          container 
          spacing={2}
          sx={{
            display: { md: 'flex', xs: 'center' },
            mt: 3
          }}
        >
          {
            (loadingKpi || isRefetching) && (
              <Grid
                item
                lg={12}
                md={12}
              >
                <CircularProgress />
              Loading KPIs
              </Grid>
            )
          }
          {
            (loadedKpi && !isRefetching) && (
              <>
                <Grid
                  item
                  lg={3} 
                  md={4}
                >
                  <KPI
                    kpiId={parseInt(id, 10)} 
                    name='Total Recorded Results' 
                    current={kpi.current.total}
                    previous={kpi.previous.total}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
                <Grid
                  item
                  lg={3} 
                  md={4}
                >
                  <KPI
                    kpiId={parseInt(id, 10)} 
                    name='Number of Positive Tests' 
                    current={kpi.current.positive}
                    previous={kpi.previous.positive}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
              </>
            )
          }
        </Box>
      </TabPanel>
      <TabPanel
        value={kpiTimeSpan}
        index={2}
      >
        <Box
          component={Grid}
          container 
          spacing={2}
          sx={{
            display: { md: 'flex', xs: 'center' },
            mt: 3
          }}
        >
          {
            (loadingKpi || isRefetching) && (
              <Grid
                item
                lg={12}
                md={12}
              >
                <CircularProgress />
              Loading KPIs
              </Grid>
            )
          }
          {
            (loadedKpi && !isRefetching) && (
              <>
                <Grid
                  item
                  lg={3} 
                  md={4}
                >
                  <KPI
                    kpiId={parseInt(id, 10)} 
                    name='Total Recorded Results' 
                    current={kpi.current.total}
                    previous={kpi.previous.total}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
                <Grid
                  item
                  lg={3} 
                  md={4}
                >
                  <KPI
                    kpiId={parseInt(id, 10)} 
                    name='Number of Positive Tests' 
                    current={kpi.current.positive}
                    previous={kpi.previous.positive}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
              </>
            )
          }
        </Box>
      </TabPanel>
      <TabPanel
        value={kpiTimeSpan}
        index={3}
      >
        <Box
          component={Grid}
          container 
          spacing={2}
          sx={{
            display: { md: 'flex', xs: 'center' },
            mt: 3
          }}
        >
          {
            (loadingKpi || isRefetching) && (
              <Grid
                item
                lg={12}
                md={12}
              >
                <CircularProgress />
              Loading KPIs
              </Grid>
            )
          }
          {
            (loadedKpi && !isRefetching) && (
              <>
                <Grid
                  item
                  lg={3} 
                  md={4}
                >
                  <KPI
                    kpiId={parseInt(id, 10)} 
                    name='Total Recorded Results' 
                    current={kpi.current.total}
                    previous={kpi.previous.total}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
                <Grid
                  item
                  lg={3} 
                  md={4}
                >
                  <KPI
                    kpiId={parseInt(id, 10)} 
                    name='Number of Positive Tests' 
                    current={kpi.current.positive}
                    previous={kpi.previous.positive}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
              </>
            )
          }
        </Box>
      </TabPanel>
      <TabPanel
        value={kpiTimeSpan}
        index={4}
      >
        <Box
          component={Grid}
          container 
          spacing={2}
          sx={{
            display: { md: 'flex', xs: 'center' },
            mt: 3
          }}
        >
          {
            (loadingKpi || isRefetching) && (
              <Grid
                item
                lg={12}
                md={12}
              >
                <CircularProgress />
              Loading KPIs
              </Grid>
            )
          }
          {
            (loadedKpi && !isRefetching) && (
              <>
                <Grid
                  item
                  lg={3} 
                  md={4}
                >
                  <KPI
                    kpiId={parseInt(id, 10)} 
                    name='Total Recorded Results' 
                    current={kpi.current.total}
                    previous={kpi.previous.total}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
                <Grid
                  item
                  lg={3} 
                  md={4}
                >
                  <KPI
                    kpiId={parseInt(id, 10)} 
                    name='Number of Positive Tests' 
                    current={kpi.current.positive}
                    previous={kpi.previous.positive}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
              </>
            )
          }
        </Box>
      </TabPanel>
      <TabPanel
        value={kpiTimeSpan}
        index={5}
      >
        <Box
          component={Grid}
          container 
          spacing={2}
          sx={{
            display: { md: 'flex', xs: 'center' },
            mt: 3
          }}
        >
          {
            (loadingKpi || isRefetching) && (
              <Grid
                item
                lg={12}
                md={12}
              >
                <CircularProgress />
              Loading KPIs
              </Grid>
            )
          }
          {
            dateRangeError ? (
              <Alert severity='error' sx={{ mt: 2 }}>
              Please enter valid date range using this format MM/DD/YYYY - MM/DD/YYYY
              </Alert>
            ) : (
              <>
                {
                  (loadedKpi && !isRefetching) && (
                    <>
                      <Grid
                        item
                        lg={3} 
                        md={4}
                      >
                        <KPI
                          kpiId={parseInt(id, 10)} 
                          name='Total Recorded Results' 
                          current={kpi.current.total}
                          previous={kpi.previous.total}
                          timeSpan={kpiTimeSpan}
                          tabs={kpiTabs}
                        />
                      </Grid>
                      <Grid
                        item
                        lg={3} 
                        md={4}
                      >
                        <KPI
                          kpiId={parseInt(id, 10)} 
                          name='Number of Positive Tests' 
                          current={kpi.current.positive}
                          previous={kpi.previous.positive}
                          timeSpan={kpiTimeSpan}
                          tabs={kpiTabs}
                        />
                      </Grid>
                    </>
                  )
                }
              </>
            )
            
          }
        </Box>
      </TabPanel>
    </div>
  )
}

export default Drilldown